import { defineStore } from 'pinia'
import { BLACKLISTED_AUTOSUB_TYPES } from '~/libs/deal-status'
import { getAcceptedOffer, normalizeOffer } from '~/libs/offer-helper'
import { compatibleDate } from '~/libs/date-helpers'
import { useDealsStore } from '~/store/deals'
import { useApprovalsStore } from '~/store/approvals'
import { useAffiliateCustomizationStore } from '~/store/affiliate-customization'
import { addDays, isAfter } from 'date-fns'
import { isEqual } from 'es-toolkit/compat'

export const useOffersStore = defineStore('offers', () => {
  const dealsStore = useDealsStore()
  const approvalsStore = useApprovalsStore()
  const affiliateCustomizationStore = useAffiliateCustomizationStore()

  /*
  ███████ ████████  █████  ████████ ███████
  ██         ██    ██   ██    ██    ██
  ███████    ██    ███████    ██    █████
       ██    ██    ██   ██    ██    ██
  ███████    ██    ██   ██    ██    ███████
  STATE
*/
/*
   ██████  ███████ ████████ ████████ ███████ ██████  ███████
  ██       ██         ██       ██    ██      ██   ██ ██
  ██   ███ █████      ██       ██    █████   ██████  ███████
  ██    ██ ██         ██       ██    ██      ██   ██      ██
   ██████  ███████    ██       ██    ███████ ██   ██ ███████
  GETTERS
*/
const offers = computed(() => {
  const isStandardExperience = !affiliateCustomizationStore.isEmbeddedLayout

  return dealsStore.deals.filter(deal => {
    return deal.stage !== 'inactive'
  }).flatMap(deal => {
    return deal.offers.filter(offer => {
      if (offer.id === deal.acceptedOffer?.id) {
        return true
      }
      if (!isStandardExperience) {
        const offerCreated = new Date(compatibleDate(offer.created))
        const expiredTime = addDays(offerCreated, 30)
        return offer.amount > 0 && expiredTime && isAfter(expiredTime, new Date())
      }
      return offer.amount > 0
    }).flatMap(offer => {
      const isAccepted = deal.acceptedOffer?.id === offer.id
      const overrides = {
        isAccepted,
        ...(isAccepted && { amount: approvalsStore.contractRequestAmounts?.[deal.id] || offer.amount })
      }
      return normalizeOffer(offer, deal, overrides)
    })
  })
})

const publishedOffers = computed(() => {
  const activeDealIds = dealsStore.deals
    .filter(deal =>
      (!deal.autoSubmitType || !BLACKLISTED_AUTOSUB_TYPES.includes(deal.autoSubmitType)) &&
      deal.loanProductOfferType === 'tier-1' &&
      ['underwriting', 'closing'].includes(deal.stage) &&
      !['declined', 'offerDeclined'].includes(deal.status)
    )
    .map(deal => deal.id)

  return offers.value.filter((offer) => (
    activeDealIds.includes(offer.dealId)
    && (Boolean(offer.publishedToBorrower) || offer.isAccepted === true)
  ))
})

let _cachedAcceptedOffer = null
const acceptedOffer = computed(() => {
  const activeDeal = dealsStore.activeDeal
  const acceptedOffer = getAcceptedOffer(activeDeal)

  const overrides = { isAccepted: true }
  if (acceptedOffer && activeDeal.stage !== 'funded') {
    const crAmount = approvalsStore.contractRequestAmounts?.[activeDeal.id] ?? null
    if (Number.isFinite(parseFloat(crAmount)) && parseFloat(acceptedOffer.amount) !== parseFloat(crAmount)) {
      overrides.amount = crAmount
    }
  }

  const normalizedOffer = acceptedOffer ? normalizeOffer(acceptedOffer, dealsStore.activeDeal, overrides) : null

  if (isEqual(normalizedOffer, _cachedAcceptedOffer)) {
    return _cachedAcceptedOffer
  }
  _cachedAcceptedOffer = normalizedOffer
  return normalizedOffer
})

const hasAcceptedOffer = computed(() => {
  return Boolean(acceptedOffer.value?.id)
})

/*
   █████   ██████ ████████ ██  ██████  ███    ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ████   ██ ██
  ███████ ██         ██    ██ ██    ██ ██ ██  ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ██  ██ ██      ██
  ██   ██  ██████    ██    ██  ██████  ██   ████ ███████
  ACTIONS
  ! - - Actions calling other actions in the same store must use `this.actionName(...)`
  ! - - If we do not use `this.actionName` it will not be properly mockable in tests.
  ! - - Computeds and refs will work fine, and should be called directly though.
*/

  return {
    //STATE
    offers,

    //GETTERS
    publishedOffers,
    acceptedOffer,
    hasAcceptedOffer,
  }
})
